import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import Button from 'flarum/common/components/Button';
import normalize from '../utils/normalize';
import editPostButton from '../utils/editPostButton';
// A static `import ... from 'ext:champis-species/...'` captures whatever
// flarum.reg holds at *this module's own* evaluation time, which isn't
// guaranteed to be after champis-species has finished registering its own
// exports (see champis-forum's DiscussionFilters.js, the other place this
// exact lazy-lookup pattern is used, for the confirmed failure that reusing
// a static import hit). Resolved lazily instead, at actual render time.
//
// Typed as `typeof Page` (any real Component subclass works as a JSX tag
// type - Page is already imported above) rather than Mithril.ComponentTypes:
// a bare ComponentTypes union has no construct/call signature TypeScript's
// own JSX checker recognizes, so `<SpeciesSearchWidget />` below fails to
// compile with it (confirmed directly - "does not have any construct or
// call signatures").
function getSpeciesSearchWidget() {
  return window.flarum.reg.get('champis-species', 'forum/components/SpeciesSearchWidget');
}

// champis-forum's startDiscussion(): the composer with this page's category
// preselected (or the log-in modal) - looked up at click time, same reason
// as getSpeciesSearchWidget() above.
function startDiscussion(tagSlug) {
  window.flarum.reg.get('champis-forum', 'forum/utils/startDiscussion')?.(tagSlug);
}

// URL category -> forum tag slug. Mirrors champis.net's own /links/:category
// paths exactly (see addOthersMenu.js for where these categories are
// linked from).
const CATEGORY_TAGS = {
  generalwebsites: 'sites-generalistes',
  ascomycota: 'ascomycetes',
  basidiomycota: 'basidiomycetes',
  frenchsocieties: 'societes-mycologiques-francaises',
  swisssocieties: 'societes-mycologiques-suisses',
  belgiansocieties: 'societes-mycologiques-belges',
  canadiansocieties: 'societes-mycologiques-canadiennes',
  various: 'divers-1'
};

// Every link post checked is a single `<p><a href="...">...</a></p>` (the
// URL, posted as its own text) - pulling just the href out gives a compact
// one-line-per-site list matching champis.net/links/:category's own
// "Name : URL" layout, rather than rendering each post body separately
// (fine for the handful of books per category, but this can be 30+ items).
function extractHref(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  const a = div.querySelector('a[href]');
  return a ? a.href : null;
}

/**
 * /links/:category - every discussion tagged with one of the eight "Liens"
 * sub-tags, one per external site (title = site name, first post = a link
 * to it) - matching champis.net/links/:category. Sorted alphabetically by
 * title.
 *
 * Up to ~35 links per category - one plain request, no pagination needed.
 */
export default class LinksPage extends Page {
  category = '';
  discussions = null;
  error = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.category = m.route.param('category');
    this.discussions = null;
    this.error = false;
    this.load();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    const category = m.route.param('category');
    if (category !== this.category) {
      this.category = category;
      this.load();
    }
  }
  categoryTitle() {
    return app.translator.trans(`champis-others.forum.links_page.title_${this.category}`);
  }
  load() {
    this.discussions = null;
    this.error = false;
    app.setTitle(this.categoryTitle());
    const tag = CATEGORY_TAGS[this.category];
    if (!tag) {
      this.discussions = [];
      m.redraw();
      return;
    }
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/discussions',
      params: {
        filter: {
          tag
        },
        page: {
          limit: 50
        },
        include: 'firstPost'
      }
    }).then(payload => {
      const pushed = app.store.pushPayload(payload);
      this.discussions = pushed.slice().sort((a, b) => normalize(a.title()).localeCompare(normalize(b.title())));
      m.redraw();
    }).catch(() => {
      this.error = true;
      m.redraw();
    });
  }
  view() {
    const rows = this.discussions || [];
    const SpeciesSearchWidget = getSpeciesSearchWidget();
    return m("div", {
      className: "Page LinksPage"
    }, m("header", {
      className: "Hero LinksHero"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "containerNarrow"
    }, m("h1", {
      className: "Hero-title"
    }, m("i", {
      className: "icon fas fa-link",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-others.forum.links_page.hero_title'))))), m("div", {
      className: "container"
    }, m("div", {
      className: "Page-container"
    }, m("div", {
      className: "Page-content"
    }, m("div", {
      className: "ChampisLinksList"
    }, m("h2", {
      className: "ChampisSpeciesSearch-label"
    }, m("i", {
      className: "icon fas fa-link",
      "aria-hidden": "true"
    }), " ", this.categoryTitle()), m("ul", null, !this.discussions && this.error ? m(Placeholder, {
      text: app.translator.trans('champis-others.forum.links_page.error')
    }) : !this.discussions ? m(LoadingIndicator, null) : rows.length === 0 ? m(Placeholder, {
      text: app.translator.trans('champis-others.forum.links_page.empty')
    }) : rows.map(discussion => this.item(discussion))))), m("div", {
      className: "Page-sidebar"
    }, SpeciesSearchWidget && m(SpeciesSearchWidget, null), this.newDiscussionButton()))));
  }

  // Same button (icon, classes, disabled/login-fallback logic, core's own
  // translator keys) as core's IndexSidebar.tsx renders on a tag page's own
  // sidebar - a plain copy rather than reusing IndexSidebar itself, which
  // would need a routeName ('index'/'tag') this page doesn't have to get
  // past champis-forum's own restrictSidebarWidgetsToIndexPages.js gate.
  // Same pattern as champis-species' DescriptionsPage.js/champis-keys'
  // DeterminationKeysPage.js/champis-others' own BooksPage.js copy of this
  // button.
  newDiscussionButton() {
    const canStartDiscussion = app.forum.attribute('canStartDiscussion') || !app.session.user;
    return m(Button, {
      icon: "fas fa-edit",
      className: "Button Button--primary IndexPage-newDiscussion",
      "aria-label": app.translator.trans(`core.forum.index.${canStartDiscussion ? 'start_discussion_button' : 'cannot_start_discussion_button'}`),
      onclick: () => startDiscussion(CATEGORY_TAGS[this.category]),
      disabled: !canStartDiscussion
    }, app.translator.trans(`core.forum.index.${canStartDiscussion ? 'start_discussion_button' : 'cannot_start_discussion_button'}`));
  }
  item(discussion) {
    const post = discussion.firstPost();
    const href = post ? extractHref(post.contentHtml() || '') : null;
    return m("li", {
      key: discussion.id()
    }, href ? m("a", {
      href: href,
      target: "_blank",
      rel: "noopener noreferrer",
      className: "SpeciesPage-referenceLink"
    }, discussion.title()) : m("span", {
      className: "SpeciesPage-referenceLink SpeciesPage-referenceLink--plain"
    }, discussion.title()), editPostButton(post));
  }
}
flarum.reg.add('champis-others', 'forum/components/LinksPage', LinksPage);